import {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {LastUserLocation} from "../utils/models/LastUserLocation";

import {LatLng, LatLngBounds, LatLngLiteral} from "leaflet";
import {DEFAULT_RADIUS_SLIDER} from "../leaflet/map";
import {getUserLocations} from "../utils/Api";

const DEFAULT_ZOOM = 3;

const europeBounds = new LatLngBounds(
    {lat: 30.524413269923986, lng: -24.521484375000004},
    {lat: 59.24341475839977, lng: 41.08886718750001}
);

const DEFAULT_CENTER: LatLngLiteral = {lat: 46.801111, lng: 8.226667};

interface View {
    location: {lat: number, lng: number},
    zoom: number,
    options: {animate: boolean}
}

interface LastUserLocationsContextInterface {
    isLoading: boolean;
    lastUserLocationsInView: LastUserLocation[];

    circleCenter: LatLng | null;
    setCircleCenter(center: LatLng | null): void;

    circleRadius: number | null;
    setCircleRadius(radius: number | null): void;

    drawCircle: boolean;
    setDrawCircle(drawCircle: boolean): void;

    zoom: number;
    setZoom(zoom: number): void;

    center: LatLngLiteral;
    setCenter(center: LatLngLiteral): void;

    textSearch: string;
    setTextSearch(textSearch: string): void;

    bound: LatLngBounds;
    setBound(bound: LatLngBounds): void;

    selectedLastUserLocationMarker: LastUserLocation | undefined;
    setSelectedLastUserLocationMarker(marker: LastUserLocation | undefined): void;

    view: View | null;
    setView(view: View | null): void;

    countrySearch: string[];
    setCountrySearch(country: string[]): void;
    fetchUserLocations(): void;
}

const lastUserLocationsDefaultContext = createContext<LastUserLocationsContextInterface>({
    isLoading: true,
    lastUserLocationsInView: [],
    circleCenter: null,
    setCircleCenter: (center: LatLng) => {
        return;
    },
    circleRadius: DEFAULT_RADIUS_SLIDER,
    setCircleRadius: (radius: number | null) => {
        return;
    },
    drawCircle: false,
    setDrawCircle: (drawCircle: boolean) => {
        return;
    },
    zoom: DEFAULT_ZOOM,
    setZoom: (zoom: number) => {
        return;
    },
    center: DEFAULT_CENTER,
    setCenter: (center: LatLngLiteral) => {
        return;
    },
    textSearch: "",
    setTextSearch(textSearch: string) {
        return;
    },
    bound: europeBounds,
    setBound: (bound: LatLngBounds) => {
        return;
    },
    selectedLastUserLocationMarker: undefined,
    setSelectedLastUserLocationMarker: (marker: LastUserLocation | undefined) => {
        return;
    },
    view: null,
    setView: (view: View | null) => {
        return;
    },
    countrySearch: [],
    setCountrySearch: (country: string[]) => {
        return;
    },
    fetchUserLocations: () => {
        return;
    }
})

export const useLastUserLocations = () => useContext(lastUserLocationsDefaultContext);

export const LastUserLocationsProvider = ({children}: { children: ReactNode }) => {
    const [lastUserLocations, setLastUserLocations] = useState<LastUserLocation[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const fetchUserLocations = async () => {
        setIsLoading(true);
        try {
            const lastUserLocations: LastUserLocation[] = await getUserLocations();
            setLastUserLocations(lastUserLocations);
        } catch (error) {
            console.error("Error fetching user locations:", error);
            setLastUserLocations([]);
        } finally {
            setIsLoading(false);
        }
    };

    const [bound, setBound] = useState<LatLngBounds>(europeBounds);
    const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM);
    const [center, setCenter] = useState<LatLngLiteral>(DEFAULT_CENTER);

    const [lastUserLocationsInView, setLastUserLocationsInView] = useState<LastUserLocation[]>([]);

    const [circleCenter, setCircleCenter] = useState<LatLng | null>(null);
    const [circleRadius, setCircleRadius] = useState<number | null>(DEFAULT_RADIUS_SLIDER);
    const [drawCircle, setDrawCircle] = useState<boolean>(false);

    const [textSearch, setTextSearch] = useState<string>("");
    const [countrySearch, setCountrySearch] = useState<string[]>([]);

    const [selectedLastUserLocationMarker, setSelectedLastUserLocationMarker] = useState<LastUserLocation | undefined>(undefined);

    const [view, setView] = useState<View | null>(null);

    useEffect(() => {
        fetchUserLocations();
    }, []);

    useEffect(() => {
        const lastUserLocationsDisplayed = getLastUserLocationsInView(bound);
        setLastUserLocationsInView(lastUserLocationsDisplayed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUserLocations]);

    useEffect(() => {
        let lastUserLocationsToDisplay: LastUserLocation[] = [];
        const lastUserLocationsInViewport = getLastUserLocationsInView(bound);
        lastUserLocationsToDisplay = [...lastUserLocationsInViewport];
        if (circleRadius && circleCenter) {
            lastUserLocationsToDisplay = getLastUserLocationsInCircle(
                circleCenter,
                circleRadius,
                lastUserLocationsToDisplay
            );
        }
        if (textSearch !== "") {
            lastUserLocationsToDisplay = getLastUserLocationsInViewByTextSearch(textSearch, lastUserLocationsToDisplay);
        }
        if (countrySearch.length > 0) {
            lastUserLocationsToDisplay = getLastUserLocationsByCountry(countrySearch, lastUserLocationsToDisplay);
        }
        setLastUserLocationsInView(lastUserLocationsToDisplay);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bound, textSearch, circleRadius, circleCenter, countrySearch]);

    const getLastUserLocationsInView = (bounds: LatLngBounds) => {
        return lastUserLocations.filter((lastUserLocation: LastUserLocation) => {
            return bounds.contains({lat: lastUserLocation.location.latitude, lng: lastUserLocation.location.longitude});
        });
    }

    const getLastUserLocationsInViewByTextSearch = (search: string, lastUserLocations: LastUserLocation[]) => {
        return lastUserLocations.filter((lastUserLocation: LastUserLocation) => {
            return (lastUserLocation.personalReference.includes(search) ||
                lastUserLocation.userDisplayName.toLowerCase().includes(search) ||
                lastUserLocation.location.formattedAddress.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().includes(search))
        });
    }

    const getLastUserLocationsInCircle = (center: LatLng, radius: number, lastUserLocations: LastUserLocation[]) => {
        return lastUserLocations.filter((lastUserLocation: LastUserLocation) => {
            return center.distanceTo({lat: lastUserLocation.location.latitude, lng: lastUserLocation.location.longitude}) < radius;
        })
    }

    const getLastUserLocationsByCountry = (countrySearch: string[], lastUserLocations: LastUserLocation[]) => {
        return lastUserLocations.filter((lastUserLocation: LastUserLocation) => {
            return countrySearch.includes(lastUserLocation.location.countryCode);
        })
    }

    const lastUserLocationsProvider: LastUserLocationsContextInterface = {
        isLoading: isLoading,
        lastUserLocationsInView: lastUserLocationsInView,
        circleCenter: circleCenter,
        setCircleCenter: setCircleCenter,
        circleRadius: circleRadius,
        setCircleRadius: setCircleRadius,
        drawCircle: drawCircle,
        setDrawCircle: setDrawCircle,
        zoom: zoom,
        setZoom: setZoom,
        center: center,
        setCenter: setCenter,
        textSearch: textSearch,
        setTextSearch: setTextSearch,
        bound: bound,
        setBound: setBound,
        selectedLastUserLocationMarker: selectedLastUserLocationMarker,
        setSelectedLastUserLocationMarker: setSelectedLastUserLocationMarker,
        view: view,
        setView: setView,
        countrySearch: countrySearch,
        setCountrySearch: setCountrySearch,
        fetchUserLocations: fetchUserLocations
    }

    return (
        <lastUserLocationsDefaultContext.Provider value={lastUserLocationsProvider}>
            {children}
        </lastUserLocationsDefaultContext.Provider>
    )
}
